'use client';

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { BusinessProfile, BusinessProfileState } from './types';
import { analyzeBusinessDream } from './analyzer';
import { isConcurrentProcessingError } from '@/lib/ai/creditErrors';

const SOFT_CONCURRENT_MESSAGE = 'Another generation is already processing. Please wait.';

export type AnalyzeAndBuildOptions = {
  /** Project id for ledger idempotency scope (use "pending" when unsaved). */
  projectId?: string;
  /**
   * @deprecated Background auto-analyze is removed. This flag is ignored and
   * never triggers a paid AI request.
   */
  background?: boolean;
};

interface BusinessProfileContextValue extends BusinessProfileState {
  /** Explicit user action only — builds Business Profile via ledger-backed AI. */
  analyzeAndBuild: (
    dream: string,
    language?: string,
    force?: boolean,
    options?: AnalyzeAndBuildOptions
  ) => Promise<BusinessProfile | null>;
  /** Apply a partial override (user edit) — merged on top of the profile */
  applyOverride: (overrides: Partial<BusinessProfile>) => void;
  /** Clear the profile (e.g. when starting a new project) */
  clearProfile: () => void;
  /** Load an existing profile from Supabase data */
  loadProfile: (profile: BusinessProfile) => void;
  /** Merge profile from Supabase without triggering re-analysis */
  setProfileDirect: (profile: BusinessProfile) => void;
}

const BusinessProfileContext = createContext<BusinessProfileContextValue | null>(null);

export function BusinessProfileProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BusinessProfileState>({
    profile: null,
    isAnalyzing: false,
    error: null,
    lastUpdated: null,
    userOverrides: {},
  });

  // Track the dream that was last analyzed to avoid redundant calls
  const lastAnalyzedDream = useRef<string>('');
  const lastAnalyzedLang = useRef<string>('en');
  /** Sync lock — set before any await to block double-click / effect races. */
  const analyzingRef = useRef(false);

  const analyzeAndBuild = useCallback(async (
    dream: string,
    language: string = 'en',
    force: boolean = false,
    options?: AnalyzeAndBuildOptions
  ): Promise<BusinessProfile | null> => {
    if (!dream.trim()) return null;

    // Defense in depth: never run paid AI from a "background" flag.
    if (options?.background) {
      return state.profile;
    }

    // Skip if same dream + language already analyzed (unless forced)
    if (
      !force &&
      lastAnalyzedDream.current === dream.trim() &&
      lastAnalyzedLang.current === language &&
      state.profile !== null
    ) {
      return state.profile;
    }

    // Synchronous in-flight lock before any await
    if (analyzingRef.current) {
      return state.profile;
    }
    analyzingRef.current = true;

    setState(prev => ({ ...prev, isAnalyzing: true, error: null }));

    try {
      const attemptId = crypto.randomUUID();
      const profile = await analyzeBusinessDream(dream, language, {
        projectId: options?.projectId?.trim() || 'pending',
        attemptId,
      });

      // Merge user overrides on top of freshly generated profile
      setState(prev => {
        const merged = { ...profile, ...prev.userOverrides } as BusinessProfile;
        return {
          ...prev,
          profile: merged,
          isAnalyzing: false,
          error: null,
          lastUpdated: new Date().toISOString(),
        };
      });

      lastAnalyzedDream.current = dream.trim();
      lastAnalyzedLang.current = language;

      return profile;
    } catch (err: unknown) {
      if (isConcurrentProcessingError(err)) {
        setState(prev => ({
          ...prev,
          isAnalyzing: false,
          error: SOFT_CONCURRENT_MESSAGE,
        }));
        return null;
      }

      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to analyze business dream';
      setState(prev => ({ ...prev, isAnalyzing: false, error: message }));
      return null;
    } finally {
      analyzingRef.current = false;
    }
  }, [state.profile]);

  const applyOverride = useCallback((overrides: Partial<BusinessProfile>) => {
    setState(prev => {
      const newOverrides = { ...prev.userOverrides, ...overrides };
      const updatedProfile = prev.profile
        ? { ...prev.profile, ...overrides }
        : null;
      return {
        ...prev,
        profile: updatedProfile,
        userOverrides: newOverrides,
        lastUpdated: new Date().toISOString(),
      };
    });
  }, []);

  const clearProfile = useCallback(() => {
    setState({
      profile: null,
      isAnalyzing: false,
      error: null,
      lastUpdated: null,
      userOverrides: {},
    });
    lastAnalyzedDream.current = '';
    lastAnalyzedLang.current = 'en';
    analyzingRef.current = false;
  }, []);

  const loadProfile = useCallback((profile: BusinessProfile) => {
    setState(prev => ({
      ...prev,
      profile,
      isAnalyzing: false,
      error: null,
      lastUpdated: profile.generated_at,
      userOverrides: {},
    }));
    lastAnalyzedDream.current = profile.dream_input ?? '';
    lastAnalyzedLang.current = profile.language ?? 'en';
  }, []);

  const setProfileDirect = useCallback((profile: BusinessProfile) => {
    setState(prev => ({
      ...prev,
      profile,
      isAnalyzing: false,
      error: null,
      lastUpdated: profile.generated_at,
    }));
    lastAnalyzedDream.current = profile.dream_input ?? '';
    lastAnalyzedLang.current = profile.language ?? 'en';
  }, []);

  const value: BusinessProfileContextValue = {
    ...state,
    analyzeAndBuild,
    applyOverride,
    clearProfile,
    loadProfile,
    setProfileDirect,
  };

  return (
    <BusinessProfileContext.Provider value={value}>
      {children}
    </BusinessProfileContext.Provider>
  );
}

export function useBusinessProfile(): BusinessProfileContextValue {
  const ctx = useContext(BusinessProfileContext);
  if (!ctx) {
    throw new Error('useBusinessProfile must be used within BusinessProfileProvider');
  }
  return ctx;
}
